import { CouponCode } from "@/components/Cart/components/CouponCode";
import LineItem from "@/components/Cart/components/LineItem";
import { CartUIDivider } from "@/components/Cart/ui";
import {
  BASE_DASHB_EVENT_PROPS,
  DASHBOARD_EVENTS,
} from "@/components/Dashboard/v2/events.dashboard";
import { usePromoCodeComponent } from "@/components/shared/PromoCode/usePromoCodeComponent";
import { ReferralCreditUI } from "@/components/shared/ReferralCreditBanner/ReferralCreditUI";
import { useReferralCreditField } from "@/components/shared/ReferralCreditBanner/useReferralCreditField";
import { ConfirmButton } from "@/components/ui/confirm-button/ConfirmButton";
import {
  DialogCard,
  DialogCardContent,
  DialogCardFooter,
  DialogCardHeader,
  DialogCardTitle,
} from "@/components/ui/dialog";
import { DotLoader } from "@/components/ui/Loader/DotLoader";
import eventTrackingHandler from "@/tracking/eventTrackingHandler";
import { TBillingInterval } from "@/types/app";
import { cn } from "@/utils/cn";
import { formatMinorAmount } from "@/utils/formatMinorAmount";
import { PRICE_UNAVAILABLE } from "@/utils/formatPriceOrNA";
import formatTemplateString from "@/utils/formatTemplateString";
import { toMajorUnits } from "@/utils/toMajorUnits";
import { ArrowRight } from "@pkg/icons";
import { useState } from "react";
import { useIntercom } from "react-use-intercom";
import { EstimateIntervalData } from "./estimate-interval-change";
import { formatEndsOn, getUnusedDaysCredit } from "./plan-period-copy";
import { PlanSwitchSuccess } from "./plan-switch-success";
import { TSwitchIntervalCardContent } from "./types.interval-switch";
import { useIntervalChangeMutation } from "./use-interval-change-mutation";

interface SwitchIntervalCardProps {
  content: TSwitchIntervalCardContent;
  planName: string;
  currency: string;
  targetInterval: TBillingInterval;
  currentPeriodEnd?: number;
  /** The yearly renewal amount, shown as the subtotal */
  subtotal?: string;
  renewNote?: string;
  estimateData?: EstimateIntervalData;
  isEstimating: boolean;
  isEstimateError: boolean;
  isV1?: boolean;
  promoCode?: string;
  footnote?: string;
  onConfirm: () => void;
  onApplyPromo?: (code: string) => Promise<void>;
  onRemovePromo?: () => Promise<void>;
}

const PlanPeriodCard = ({
  label,
  planName,
  endsOn,
  isHighlighted,
}: {
  label: string;
  planName: string;
  endsOn?: string;
  isHighlighted?: boolean;
}) => (
  <div
    className={cn(
      "flex flex-1 flex-col gap-0.5 rounded-lg border p-3",
      isHighlighted
        ? "border-accent-blue bg-white"
        : "border-edge-light bg-surface-muted",
    )}
  >
    <span className="font-archivo text-[10px] leading-4 font-semibold tracking-[0.5px] text-type-2 uppercase">
      {label}
    </span>
    <span className="font-archivo text-base leading-tight font-semibold text-type-1">
      {planName}
    </span>
    {endsOn && (
      <span className="font-archivo text-sm leading-tight font-light text-type-1">
        {endsOn}
      </span>
    )}
  </div>
);

const DueTodayAmount = ({
  isEstimating,
  hasError,
  amountMinor,
  currency,
}: {
  isEstimating: boolean;
  hasError: boolean;
  amountMinor: number;
  currency: string;
}) => {
  if (isEstimating) {
    return <DotLoader size={32} />;
  }

  if (hasError) {
    return (
      <span className="font-archivo text-base text-error-primary">
        {PRICE_UNAVAILABLE}
      </span>
    );
  }

  return (
    <span className="font-archivo text-[34px] leading-tight font-normal text-type-1">
      {formatMinorAmount(amountMinor, currency)}
    </span>
  );
};

export const SwitchIntervalCard = ({
  content,
  planName,
  currency,
  targetInterval,
  currentPeriodEnd,
  subtotal,
  renewNote,
  estimateData,
  isEstimating,
  isEstimateError,
  isV1 = false,
  promoCode,
  footnote,
  onConfirm,
  onApplyPromo,
  onRemovePromo,
}: SwitchIntervalCardProps) => {
  const [isActionSuccess, setIsActionSuccess] = useState(false);
  const { showNewMessage } = useIntercom();

  const intervalChangeMutation = useIntervalChangeMutation({
    isV1,
    onSuccess: () => {
      setIsActionSuccess(true);
      onConfirm?.();
    },
    onError: () => {
      showNewMessage(content.supportMessage);
    },
  });

  const isPending = intervalChangeMutation.isPending;
  const hasError = isEstimateError || intervalChangeMutation.isError;
  const discounts = estimateData?.discounts ?? [];
  const isZeroAmount =
    estimateData !== undefined && estimateData.amount_due === 0;

  const unusedDaysCredit = getUnusedDaysCredit(
    content.unusedDaysCredit,
    estimateData?.unused_days,
  );

  const promo = usePromoCodeComponent({
    discounts,
    onApplyPromoCode: async (code) => {
      await onApplyPromo?.(code);
      eventTrackingHandler(DASHBOARD_EVENTS.INTERVAL_SWITCH_PROMO_APPLIED, {
        ...BASE_DASHB_EVENT_PROPS,
        types: ["posthog", "customerio"],
        properties: { promo_code: code, target_interval: targetInterval },
      });
    },
    onRemoveDiscount: async (code) => {
      await onRemovePromo?.();
      eventTrackingHandler(DASHBOARD_EVENTS.INTERVAL_SWITCH_PROMO_REMOVED, {
        ...BASE_DASHB_EVENT_PROPS,
        types: ["posthog", "customerio"],
        properties: { promo_code: code, target_interval: targetInterval },
      });
    },
    isApplying: isEstimating,
    isDisabled: isPending || isEstimating,
    initialInputValue: promoCode,
  });

  const appliedDiscountCodes = promo.appliedDiscounts.map(
    (discount) => discount.code,
  );

  // V1: the estimate doesn't echo the coupon back in `discounts`, so the prop drives the applied state.
  const hasValidDiscount = isV1 ? Boolean(promoCode) : promo.hasValidDiscount;
  const appliedStates =
    isV1 && promoCode
      ? [
          {
            code: promoCode,
            message: content.couponApplied,
            onRemove: () => onRemovePromo?.(),
          },
        ]
      : promo.appliedDiscounts.map((discount) => ({
          ...discount,
          message: content.couponApplied,
        }));

  const displayCurrency = estimateData?.currency ?? currency;
  const credit = useReferralCreditField({
    referralCredits: estimateData?.referral_credits,
    currency: displayCurrency,
    disabled: isPending || isEstimating,
  });

  const amountDueMinor =
    credit.isUsed &&
    estimateData?.amount_due_with_referral_credits !== undefined
      ? estimateData.amount_due_with_referral_credits
      : (estimateData?.amount_due ?? 0);

  const handleConfirmButton = () => {
    const effectivePromoCode = isV1 ? promoCode : appliedDiscountCodes[0];

    intervalChangeMutation.mutate({
      targetInterval,
      promoCode: effectivePromoCode,
      isReferralCreditUsed: credit.isUsed,
    });

    eventTrackingHandler(DASHBOARD_EVENTS.PLAN_SWITCH_CONFIRMED, {
      types: ["posthog", "customerio"],
      properties: {
        ...BASE_DASHB_EVENT_PROPS,
        target_interval: targetInterval,
        amount_due: toMajorUnits(amountDueMinor, displayCurrency),
        currency: displayCurrency,
        has_promo_code: !!effectivePromoCode,
        promo_codes: effectivePromoCode ? [effectivePromoCode] : [],
        is_referral_credit_used: credit.isUsed,
        referral_credit_amount: credit.isUsed ? credit.claimableCredit : 0,
      },
    });
  };

  if (isActionSuccess) {
    return <PlanSwitchSuccess />;
  }

  return (
    <DialogCard className="max-w-none gap-5">
      <DialogCardHeader>
        <DialogCardTitle className="font-semibold text-type-1 sm:text-base">
          {content.title}
        </DialogCardTitle>
      </DialogCardHeader>

      <DialogCardContent className="gap-5">
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <PlanPeriodCard
            label={content.nowLabel}
            planName={planName}
            endsOn={formatEndsOn(content.endsOn, currentPeriodEnd)}
          />
          <ArrowRight className="mx-auto size-4 shrink-0 rotate-90 text-type-2 sm:rotate-0" />
          <PlanPeriodCard
            label={content.afterRenewalLabel}
            planName={planName}
            endsOn={formatEndsOn(
              content.endsOn,
              estimateData?.next_billing_date,
            )}
            isHighlighted
          />
        </div>

        <div className="flex flex-col gap-3 rounded-xl bg-surface-muted p-4">
          <h3 className="font-archivo text-base font-semibold text-type-1">
            {content.whatYouPay}
          </h3>

          {subtotal && (
            <LineItem
              keyName={content.subtotal}
              value={subtotal}
              isLoading={isEstimating}
            />
          )}

          <CouponCode
            isApplying={promo.isApplying}
            currency={displayCurrency}
            {...(!hasValidDiscount &&
              !isZeroAmount && {
                field: {
                  placeholder: content.promoCode.placeholder,
                  value: promo.inputValue,
                  setValue: promo.setInputValue,
                  codeInvalid: promo.isInvalidState,
                },
                cta: {
                  label: content.promoCode.applyText,
                  action: promo.handleApply,
                  disabled: promo.isDisabled,
                },
              })}
            appliedStates={appliedStates}
          />

          {!isZeroAmount && credit.hasCredits && (
            <ReferralCreditUI {...credit.bannerProps} />
          )}

          {unusedDaysCredit && (
            <LineItem
              keyName={unusedDaysCredit.label}
              value={unusedDaysCredit.value}
              isLoading={isEstimating}
              keyClassName="normal-case"
              valueClassName="text-xs italic text-type-2"
            />
          )}

          {!!estimateData?.tax_amount && (
            <LineItem
              keyName={
                estimateData.tax_rate_percentage
                  ? formatTemplateString(
                      content.taxWithRate,
                      String(estimateData.tax_rate_percentage),
                    )
                  : content.tax
              }
              value={formatMinorAmount(
                estimateData.tax_amount,
                displayCurrency,
              )}
              isLoading={isEstimating}
            />
          )}

          <CartUIDivider />

          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-0.5">
              <span className="font-archivo text-base leading-relaxed font-semibold tracking-[0.16px] text-type-1">
                {content.dueToday}
              </span>
              {renewNote && (
                <span className="font-archivo text-xs leading-tight font-light tracking-[0.24px] text-type-1 italic">
                  {renewNote}
                </span>
              )}
            </div>
            <DueTodayAmount
              isEstimating={isEstimating}
              hasError={hasError}
              amountMinor={amountDueMinor}
              currency={displayCurrency}
            />
          </div>
        </div>

        {hasError && (
          <p className="font-archivo text-sm text-error-primary">
            {content.errorMessage}
          </p>
        )}
      </DialogCardContent>

      <DialogCardFooter className="flex-col">
        <ConfirmButton
          isConfirming={isPending}
          onClick={handleConfirmButton}
          label={content.checkoutButton}
          disabled={hasError || isEstimating}
          className="w-full"
        />

        {footnote && (
          <p className="self-start font-archivo text-sm text-type-2">
            {footnote}
          </p>
        )}
      </DialogCardFooter>
    </DialogCard>
  );
};
